/**
 * Master-P3 Final — Budget Wizard.
 *
 * User-facing configuration surface for the tenant's business budget.
 * Route: /app/settings/budget (linked from Settings).
 * Consumes:
 *   GET  /api/v1/budgets/current    — active budget config
 *   GET  /api/v1/budgets/status     — live enforcement + spend snapshot
 *   POST /api/v1/budgets            — first-time create
 *   PATCH /api/v1/budgets/current   — updates (auto-versioned)
 *   POST /api/v1/budgets/current/{pause,resume,end}
 *
 * Design intent: this is the *only* budget-config surface. All
 * cost-incurring actions in the platform read from the same source
 * of truth via the ``BudgetService`` on the backend.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  ads: "Werbung (Meta/Google)",
  llm: "Text-LLM",
  images: "Bildgenerierung",
  videos: "Videogenerierung",
  research: "Research-Provider",
};
const CATEGORY_ORDER = ["ads", "llm", "images", "videos", "research"];
const IANA_ZONES = [
  "Europe/Berlin", "Europe/Zurich", "Europe/Vienna",
  "Europe/London", "UTC",
];


function euros(cents) {
  if (cents == null) return "—";
  return (Number(cents) / 100).toFixed(2) + " €";
}


function StaleBadge({ atIso, toleranceMinutes = 30 }) {
  if (!atIso) return null;
  const ageMin = (Date.now() - new Date(atIso).getTime()) / 60000;
  const stale = ageMin > toleranceMinutes;
  return (
    <span data-testid="budget-freshness-badge"
      style={{ fontSize: 11, padding: "2px 6px", borderRadius: 4,
        marginLeft: 6,
        background: stale ? "#3a1010" : "#0a2a10",
        color: stale ? "#f0b0b0" : "#a0d0a0" }}>
      {stale ? `stale (${Math.round(ageMin)}m)` : "aktuell"}
    </span>
  );
}


function StatusPill({ status }) {
  const map = {
    active: { bg: "#0e4429", label: "Aktiv" },
    paused: { bg: "#5a4a10", label: "Pausiert" },
  };
  const s = map[status] || { bg: "#3a3a3a", label: status || "—" };
  return (
    <span data-testid={`budget-status-pill-${status}`}
      style={{ padding: "2px 8px", borderRadius: 12,
        background: s.bg, color: "#fff", fontSize: 12 }}>
      {s.label}
    </span>
  );
}


function BudgetForm({ initial, onSubmit, submitting }) {
  const [form, setForm] = useState(() => ({
    currency: initial?.currency || "EUR",
    daily_euros: initial ? (initial.daily_budget_amount_cents / 100)
                            : 30,
    total_euros: initial?.total_budget_amount_cents != null
      ? initial.total_budget_amount_cents / 100 : "",
    starts_at: initial?.starts_at
      ? initial.starts_at.slice(0, 16)
      : new Date(Date.now() - 60_000).toISOString().slice(0, 16),
    ends_at: initial?.ends_at ? initial.ends_at.slice(0, 16) : "",
    timezone_iana: initial?.timezone_iana || "Europe/Berlin",
    sub_ads: initial?.sub_budgets?.ads != null
      ? initial.sub_budgets.ads / 100 : "",
    sub_llm: initial?.sub_budgets?.llm != null
      ? initial.sub_budgets.llm / 100 : "",
    sub_images: initial?.sub_budgets?.images != null
      ? initial.sub_budgets.images / 100 : "",
    sub_videos: initial?.sub_budgets?.videos != null
      ? initial.sub_budgets.videos / 100 : "",
    sub_research: initial?.sub_budgets?.research != null
      ? initial.sub_budgets.research / 100 : "",
    status: initial?.status || "active",
    change_reason: "",
  }));

  const handle = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const cents = (v) => (v === "" || v == null)
      ? null : Math.round(Number(v) * 100);
    const startsAtIso = new Date(form.starts_at).toISOString();
    const endsAtIso = form.ends_at
      ? new Date(form.ends_at).toISOString() : null;
    const payload = {
      currency: form.currency,
      daily_budget_amount_cents: cents(form.daily_euros),
      total_budget_amount_cents: cents(form.total_euros),
      starts_at: startsAtIso,
      ends_at: endsAtIso,
      timezone_iana: form.timezone_iana,
      sub_budgets: {
        ads: cents(form.sub_ads), llm: cents(form.sub_llm),
        images: cents(form.sub_images), videos: cents(form.sub_videos),
        research: cents(form.sub_research),
      },
      status: form.status,
      change_reason: form.change_reason ||
        (initial ? "manual update" : "initial budget"),
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} data-testid="budget-form"
      style={{ display: "grid", gap: 12, gridTemplateColumns:
                "repeat(2, minmax(0,1fr))" }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>
          Tagesbudget (€, brutto über alle Kategorien)
        </span>
        <input data-testid="budget-input-daily" type="number" min="0"
          step="0.01" value={form.daily_euros}
          onChange={handle("daily_euros")} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>
          Gesamtbudget (€, optional; leer = kein Cap)
        </span>
        <input data-testid="budget-input-total" type="number" min="0"
          step="0.01" value={form.total_euros}
          onChange={handle("total_euros")} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>Start (lokal)</span>
        <input data-testid="budget-input-starts-at"
          type="datetime-local"
          value={form.starts_at} onChange={handle("starts_at")}
          required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>
          Ende (lokal, optional)
        </span>
        <input data-testid="budget-input-ends-at" type="datetime-local"
          value={form.ends_at} onChange={handle("ends_at")} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>Zeitzone (IANA)</span>
        <select data-testid="budget-input-timezone"
          value={form.timezone_iana}
          onChange={handle("timezone_iana")}>
          {IANA_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>Status</span>
        <select data-testid="budget-input-status"
          value={form.status} onChange={handle("status")}>
          <option value="active">active</option>
          <option value="paused">paused</option>
        </select>
      </label>

      <fieldset style={{ gridColumn: "1 / -1", border: "1px solid #333",
        borderRadius: 6, padding: 12 }}>
        <legend style={{ fontSize: 12, color: "#888",
          padding: "0 6px" }}>
          Unterbudgets (€ pro Tag, optional — Summe ≤ Tagesbudget)
        </legend>
        <div style={{ display: "grid", gap: 8,
          gridTemplateColumns: "repeat(5, minmax(0,1fr))" }}>
          {CATEGORY_ORDER.map((c) => (
            <label key={c}
              style={{ display: "flex", flexDirection: "column",
                gap: 4, fontSize: 12 }}>
              {CATEGORY_LABELS[c]}
              <input data-testid={`budget-input-sub-${c}`}
                type="number" min="0" step="0.01"
                value={form[`sub_${c}`]}
                onChange={handle(`sub_${c}`)} />
            </label>))}
        </div>
      </fieldset>

      <label style={{ gridColumn: "1 / -1",
        display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#888" }}>
          Änderungsgrund (audit-log-pflichtig)
        </span>
        <input data-testid="budget-input-change-reason"
          value={form.change_reason}
          onChange={handle("change_reason")}
          placeholder={initial ? "z.B. Cap für Kampagne X gesenkt"
                                     : "Erstanlage"} />
      </label>

      <div style={{ gridColumn: "1 / -1", display: "flex",
        gap: 8, alignItems: "center" }}>
        <button data-testid="budget-submit" type="submit"
          disabled={submitting}
          style={{ padding: "8px 16px", background: "#0e4429",
            color: "#fff", border: "1px solid #1a6f47",
            borderRadius: 4,
            cursor: submitting ? "not-allowed" : "pointer" }}>
          {submitting ? "Wird gespeichert…"
                       : initial ? "Speichern" : "Budget anlegen"}
        </button>
        <span style={{ fontSize: 11, color: "#888" }}>
          Wirksam ab jetzt — nächster reserve/check-Aufruf sieht den
          neuen Rahmen sofort.
        </span>
      </div>
    </form>
  );
}


function StatusPanel({ status, budget }) {
  if (!status) return null;
  const t = status.totals_cents || {};
  return (
    <div data-testid="budget-status-panel" style={{ marginTop: 16 }}>
      <div style={{ display: "grid",
        gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 10 }}>
        <StatusTile testid="tile-budget" label="Tagesbudget"
          value={euros(t.budget_daily)} />
        <StatusTile testid="tile-spent" label="Erfasster Verbrauch"
          value={euros(t.spent_real)} />
        <StatusTile testid="tile-reserved" label="Offene Reservierungen"
          value={euros(t.reserved_real)} />
        <StatusTile testid="tile-unreconciled"
          label="Ungeklärt ausstehend"
          value={euros(t.unreconciled_real)} />
        <StatusTile testid="tile-available" label="Verfügbar"
          value={euros(t.available_real)} highlight />
        <StatusTile testid="tile-freshness" label="Aktualität"
          value={<span>Sync-Zeit
            <StaleBadge atIso={budget?.created_at} /></span>} />
      </div>

      <div style={{ marginTop: 16, display: "grid",
        gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div data-testid="budget-status-real"
          style={{ border: "1px solid #1a6f47", borderRadius: 6,
            padding: 12 }}>
          <h4 style={{ margin: "0 0 8px 0",
            fontSize: 13, color: "#a0d0a0" }}>
            Real-Money (Enforcement-relevant)
          </h4>
          <table style={{ width: "100%", fontSize: 12 }}>
            <thead>
              <tr>
                <th align="left">Kategorie</th>
                <th align="right">Spent</th>
                <th align="right">Reserved</th>
                <th align="right">Sub-Cap</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((c) => {
                const cat = status.categories?.[c] || {};
                return (
                  <tr key={c}
                    data-testid={`budget-cat-row-real-${c}`}>
                    <td>{CATEGORY_LABELS[c]}</td>
                    <td align="right">{euros(cat.spent_cents)}</td>
                    <td align="right">{euros(cat.reserved_cents)}</td>
                    <td align="right">
                      {cat.sub_cap_cents != null
                        ? euros(cat.sub_cap_cents) : "—"}
                    </td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>

        <div data-testid="budget-status-sim"
          style={{ border: "1px dashed #5a4a10", borderRadius: 6,
            padding: 12 }}>
          <h4 style={{ margin: "0 0 8px 0",
            fontSize: 13, color: "#e8b400" }}>
            Simulation (Stub — nicht enforcement-relevant)
          </h4>
          <table style={{ width: "100%", fontSize: 12 }}>
            <thead>
              <tr>
                <th align="left">Kategorie</th>
                <th align="right">Sim-Spent</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((c) => (
                <tr key={c} data-testid={`budget-cat-row-sim-${c}`}>
                  <td>{CATEGORY_LABELS[c]}</td>
                  <td align="right">
                    {euros(status.categories?.[c]?.sim_spent_cents || 0)}
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function StatusTile({ label, value, highlight, testid }) {
  return (
    <div data-testid={testid}
      style={{ border: "1px solid #333", borderRadius: 6,
        padding: 10,
        background: highlight ? "#0a2a10" : "#111" }}>
      <div style={{ fontSize: 10, color: "#888",
        textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, marginTop: 4,
        color: highlight ? "#a0d0a0" : "#fff" }}>
        {value}
      </div>
    </div>
  );
}


export default function Budget() {
  const [budget, setBudget] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    return Promise.all([
      api.get("/budgets/current"),
      api.get("/budgets/status"),
    ]).then(([r1, r2]) => {
      setBudget(r1.data.budget || null);
      setStatus(r2.data);
      setError(null);
    }).catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = (payload) => {
    setSaving(true);
    const call = budget
      ? api.patch("/budgets/current", payload)
      : api.post("/budgets", payload);
    call.then(() => {
      toast.success("Wirksam ab jetzt");
      return load();
    }).catch((e) => {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.reason || detail?.error || e.message);
    }).finally(() => setSaving(false));
  };

  return (
    <div data-testid="budget-page"
      style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      <header style={{ marginBottom: 16, display: "flex",
        alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h2 data-testid="budget-title" style={{ margin: 0 }}>
            Budgetverwaltung
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "#888", fontSize: 13 }}>
            Ein aktives Budget pro Tenant. Jede Änderung ist versioniert
            und im Audit-Log nachvollziehbar.
          </p>
        </div>
        {budget && <StatusPill status={budget.status} />}
      </header>

      <div data-testid="budget-provider-banner"
        style={{ background: "#1a3a5c", color: "#c0d8ff",
          borderRadius: 6, padding: 10, fontSize: 12,
          marginBottom: 16 }}>
        Anbieterlimit: Meta/Google/OpenAI haben eigene Rate- &amp;
        Kosten-Caps auf Anbieterseite — <b>App-Limit ist maßgeblich</b>.
        Verzögerte Abrechnung möglich; absolute Ausgabengarantie nur
        bei nachgewiesenem Anbieter-Cap.
      </div>

      {loading && <p data-testid="budget-loading">Lädt…</p>}
      {error && (
        <p data-testid="budget-error" style={{ color: "#f0b0b0" }}>
          {String(error)}
        </p>
      )}

      {!loading && (
        <>
          <BudgetForm initial={budget} onSubmit={submit}
            submitting={saving} />
          <StatusPanel status={status} budget={budget} />
        </>
      )}
    </div>
  );
}
